import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  deleteEquipmentImage,
  getEquipmentImageBlob,
  getEquipmentImages,
  uploadEquipmentImage,
} from "../../api/warehouse/equipmentImage";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { EquipmentImageResponse } from "../../types/model/warehouse/equipmentImage";

interface ImageWithBlob {
  image: EquipmentImageResponse;
  blob: Blob;
}

const STALE_TIME = 5 * 60 * 1000;
const imagesKey = (equipmentId: number) => ["equipment-images", equipmentId] as const;

export const useEquipmentImages = (equipmentId: number, enabled: boolean) => {
  const handleError = useErrorHandler();
  const queryClient = useQueryClient();
  const [images, setImages] = useState<EquipmentImageResponse[]>([]);

  const query = useQuery({
    queryKey: imagesKey(equipmentId),
    queryFn: async (): Promise<ImageWithBlob[]> => {
      const list = await getEquipmentImages(equipmentId);
      return Promise.all(
        list.map(async (image) => ({ image, blob: await getEquipmentImageBlob(equipmentId, image.id) })),
      );
    },
    enabled,
    staleTime: STALE_TIME,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File): Promise<ImageWithBlob> => {
      const image = await uploadEquipmentImage(equipmentId, file);
      return { image, blob: await getEquipmentImageBlob(equipmentId, image.id) };
    },
    onSuccess: (created) =>
      queryClient.setQueryData<ImageWithBlob[]>(imagesKey(equipmentId), (prev = []) => [...prev, created]),
  });

  const deleteMutation = useMutation({
    mutationFn: (imageId: number) => deleteEquipmentImage(equipmentId, imageId),
    onSuccess: (_, imageId) =>
      queryClient.setQueryData<ImageWithBlob[]>(imagesKey(equipmentId), (prev = []) =>
        prev.filter((item) => item.image.id !== imageId),
      ),
  });

  useEffect(() => {
    const mapped = (query.data ?? []).map(({ image, blob }) => ({ ...image, url: URL.createObjectURL(blob) }));
    setImages(mapped);
    return () => mapped.forEach((image) => URL.revokeObjectURL(image.url));
  }, [query.data]);

  useEffect(() => {
    if (query.error) handleError(query.error);
  }, [query.error]);

  const upload = async (file: File) => {
    try {
      await uploadMutation.mutateAsync(file);
    } catch (error) {
      handleError(error);
    }
  };

  const remove = async (imageId: number) => {
    try {
      await deleteMutation.mutateAsync(imageId);
    } catch (error) {
      handleError(error);
    }
  };

  return {
    images,
    isLoading: query.isLoading,
    isUploading: uploadMutation.isPending,
    deletingId: deleteMutation.isPending ? (deleteMutation.variables ?? null) : null,
    upload,
    remove,
  };
};